/**
 * PageIndicatorDots — paging dots indicator.
 *
 * Ported from client-native/src/ui/molecules/PageIndicatorDots.tsx (source of truth).
 * Row of dots, gap 8, container paddingVertical 12, centered. Active dot uses
 * --color-text-primary (optionally expanded to a 3x-width pill); inactive dots
 * are rgba(0,0,0,0.2). Hidden when totalCount <= 1.
 */
import React from "react";

const SIZE_MAP = {
  small: 6,
  medium: 8,
  large: 10,
} as const;

export interface PageIndicatorDotsProps {
  /** Total number of pages/dots */
  totalCount: number;
  /** Current active index (0-based) */
  currentIndex: number;
  /** Size of the dots */
  size?: keyof typeof SIZE_MAP;
  /** Color for active dot */
  activeColor?: string;
  /** Color for inactive dots */
  inactiveColor?: string;
  /** Whether active dot should be wider (pill shape) */
  expandActive?: boolean;
  /** Called when a dot is pressed */
  onDotPress?: (index: number) => void;
}

const PageIndicatorDots: React.FC<PageIndicatorDotsProps> = ({
  totalCount,
  currentIndex,
  size = "medium",
  activeColor = "var(--color-text-primary)",
  inactiveColor = "rgba(0,0,0,0.2)",
  expandActive = true,
  onDotPress,
}) => {
  if (totalCount <= 1) {
    return null;
  }

  const dotSize = SIZE_MAP[size];
  const expandedWidth = dotSize * 3;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        gap: 8,
        paddingTop: 12,
        paddingBottom: 12,
      }}
    >
      {Array.from({ length: totalCount }).map((_, index) => {
        const isActive = index === currentIndex;
        // The visible dot/pill width participates in layout (matches iOS, where
        // the Pressable IS the dot and the expanded pill takes real space).
        const renderedWidth = isActive && expandActive ? expandedWidth : dotSize;
        const dotStyle: React.CSSProperties = {
          width: renderedWidth,
          height: dotSize,
          borderRadius: dotSize / 2,
          backgroundColor: isActive ? activeColor : inactiveColor,
          transition: "width 200ms ease, background-color 200ms ease",
          flexShrink: 0,
        };

        if (onDotPress) {
          const hitArea = 44;
          return (
            <button
              key={index}
              type="button"
              aria-label={`Go to page ${index + 1}`}
              aria-current={isActive ? "true" : undefined}
              onClick={() => onDotPress(index)}
              style={{
                // Provide a >=44px hit area, but negate the overflow with margin
                // so the layout slot equals the visible pill width (iOS parity).
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: hitArea,
                height: hitArea,
                padding: 0,
                marginLeft: -(hitArea - renderedWidth) / 2,
                marginRight: -(hitArea - renderedWidth) / 2,
                marginTop: -(hitArea - dotSize) / 2,
                marginBottom: -(hitArea - dotSize) / 2,
                border: "none",
                background: "transparent",
                cursor: "pointer",
                transition: "margin 200ms ease",
              }}
            >
              <span style={dotStyle} />
            </button>
          );
        }

        return <div key={index} style={dotStyle} aria-hidden="true" />;
      })}
    </div>
  );
};

export default PageIndicatorDots;
